/* ============================================================
   EMBERA — shared theme behaviour
   Header, mobile nav, GSAP scroll reveal, 3D tilt, magnetic
   buttons, split-title, accordion, tabs, compare slider,
   counters, toast, and the real Shopify cart (AJAX Cart API).
   ============================================================ */

(function () {
  "use strict";

  const $  = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ---------------- image fallback ---------------- */
  document.addEventListener(
    "error",
    function (e) {
      const t = e.target;
      if (t && t.tagName === "IMG" && t.src.indexOf("placeholder.svg") === -1 && window.EmberaPlaceholder) {
        t.src = window.EmberaPlaceholder;
        t.classList.add("img-fallback");
      }
    },
    true
  );

  /* ---------------- header ---------------- */
  function initHeader() {
    const header = $(".e-header");
    if (!header) return;
    const onScroll = () => header.classList.toggle("is-scrolled", window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const burger = $(".e-burger");
    const mobileNav = $(".e-mobile-nav");
    if (burger && mobileNav) {
      burger.addEventListener("click", () => {
        const open = burger.classList.toggle("is-open");
        mobileNav.classList.toggle("is-open", open);
        document.body.style.overflow = open ? "hidden" : "";
      });
      $$("a", mobileNav).forEach((a) =>
        a.addEventListener("click", () => {
          burger.classList.remove("is-open");
          mobileNav.classList.remove("is-open");
          document.body.style.overflow = "";
        })
      );
    }
  }

  /* ---------------- GSAP setup ---------------- */
  const hasGSAP = typeof window.gsap !== "undefined";
  const hasScrollTrigger = hasGSAP && typeof window.ScrollTrigger !== "undefined";
  const reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (hasScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  /* ---------------- scroll reveal ---------------- */
  function initReveal() {
    const singles = $$("[data-reveal]:not([data-reveal-bound])");
    const groups = $$("[data-reveal-group]:not([data-reveal-bound])");
    if (!singles.length && !groups.length) return;

    if (reducedMotion || !hasScrollTrigger) {
      const all = singles.concat(groups);
      if (!("IntersectionObserver" in window) || reducedMotion) {
        all.forEach((el) => {
          el.classList.add("is-in");
          el.setAttribute("data-reveal-bound", "1");
        });
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-in");
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.14, rootMargin: "0px 0px -60px 0px" }
      );
      all.forEach((el) => {
        el.setAttribute("data-reveal-bound", "1");
        io.observe(el);
      });
      return;
    }

    singles.forEach((el) => {
      el.setAttribute("data-reveal-bound", "1");
      gsap.fromTo(
        el,
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" } }
      );
    });

    groups.forEach((group) => {
      group.setAttribute("data-reveal-bound", "1");
      const kids = Array.from(group.children);
      gsap.fromTo(
        kids,
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", stagger: 0.09, scrollTrigger: { trigger: group, start: "top 85%", toggleActions: "play none none reverse" } }
      );
    });
  }

  /* ---------------- 3D tilt ---------------- */
  function initTilt() {
    if (reducedMotion) return;
    $$("[data-tilt]:not([data-tilt-bound])").forEach((el) => {
      el.setAttribute("data-tilt-bound", "1");
      const max = parseFloat(el.dataset.tiltMax) || 7;
      const lift = el.dataset.tiltLift !== "0";
      el.addEventListener("pointermove", (e) => {
        if (e.pointerType === "touch") return;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        if (hasGSAP) {
          gsap.to(el, { rotationY: px * max * 2, rotationX: -py * max * 2, transformPerspective: 900, scale: lift ? 1.02 : 1, duration: 0.5, ease: "power2.out" });
        } else {
          el.style.transform = `perspective(900px) rotateY(${px * max * 2}deg) rotateX(${-py * max * 2}deg)`;
        }
      });
      el.addEventListener("pointerleave", () => {
        if (hasGSAP) gsap.to(el, { rotationY: 0, rotationX: 0, scale: 1, duration: 0.7, ease: "power3.out" });
        else el.style.transform = "none";
      });
    });
  }

  /* ---------------- magnetic primary buttons ---------------- */
  function initMagnetic() {
    if (reducedMotion || !hasGSAP) return;
    $$(".e-btn--primary:not([data-magnetic-bound])").forEach((btn) => {
      btn.setAttribute("data-magnetic-bound", "1");
      btn.addEventListener("pointermove", (e) => {
        if (e.pointerType === "touch") return;
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        gsap.to(btn, { x: x * 0.22, y: y * 0.35, duration: 0.3, ease: "power2.out" });
      });
      btn.addEventListener("pointerleave", () => gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: "elastic.out(1, 0.4)" }));
    });
  }

  /* ---------------- hero split-title ---------------- */
  function splitTitle(el) {
    if (!el || el.dataset.splitBound) return;
    el.dataset.splitBound = "1";
    const words = el.textContent.trim().split(/\s+/);
    el.innerHTML = words.map((w) => `<span class="e-split-word">${w}</span>`).join(" ");
    if (reducedMotion || !hasGSAP) return;
    gsap.from($$(".e-split-word", el), { opacity: 0, y: "100%", duration: 0.9, ease: "power4.out", stagger: 0.045, delay: 0.1 });
  }
  function initHeroSplit() {
    $$("[data-split-title]").forEach(splitTitle);
  }

  /* ---------------- hero image parallax ---------------- */
  function initParallax() {
    if (reducedMotion || !hasScrollTrigger) return;
    $$(".e-hero__media-frame img").forEach((img) => {
      const hero = img.closest(".e-hero");
      if (!hero) return;
      gsap.set(img, { scale: 1.14, transformOrigin: "center center" });
      gsap.to(img, { yPercent: -9, ease: "none", scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: 0.6 } });
    });
  }

  /* ---------------- accordion (FAQ) ---------------- */
  function initAccordion(root) {
    $$(".e-faq__item", root).forEach((item) => {
      const btn = $(".e-faq__q", item);
      const panel = $(".e-faq__a", item);
      if (!btn || !panel || btn.dataset.bound) return;
      btn.dataset.bound = "1";
      btn.addEventListener("click", () => {
        const isOpen = item.classList.contains("is-open");
        $$(".e-faq__item", item.parentElement).forEach((other) => {
          other.classList.remove("is-open");
          $(".e-faq__a", other).style.maxHeight = null;
        });
        if (!isOpen) {
          item.classList.add("is-open");
          panel.style.maxHeight = panel.scrollHeight + "px";
        }
      });
    });
  }

  /* ---------------- tabs ---------------- */
  function initTabs(root) {
    $$(".e-tabs", root).forEach((tabs) => {
      if (tabs.dataset.bound) return;
      tabs.dataset.bound = "1";
      const btns = $$(".e-tabs__btn", tabs);
      btns.forEach((btn) => {
        btn.addEventListener("click", () => {
          btns.forEach((b) => b.classList.remove("is-active"));
          $$(".e-tabs__panel", tabs).forEach((p) => p.classList.remove("is-active"));
          btn.classList.add("is-active");
          $("#" + btn.dataset.tab, tabs).classList.add("is-active");
        });
      });
    });
  }

  /* ---------------- before/after compare slider ---------------- */
  function initCompare() {
    $$(".e-compare").forEach((el) => {
      if (el.dataset.bound) return;
      el.dataset.bound = "1";
      const after = $(".e-compare__after", el);
      const line = $(".e-compare__line", el);
      const handle = $(".e-compare__handle", el);
      const range = $(".e-compare__range", el);
      if (!after || !range) return;
      const set = (pct) => {
        pct = Math.max(0, Math.min(100, pct));
        after.style.clipPath = `inset(0 0 0 ${pct}%)`;
        if (line) line.style.left = pct + "%";
        if (handle) handle.style.left = pct + "%";
        range.value = pct;
      };
      range.addEventListener("input", (e) => set(Number(e.target.value)));
      set(Number(range.value || 50));
    });
  }

  /* ---------------- animated counters ---------------- */
  function initCounters() {
    const els = $$("[data-count]:not([data-count-bound])");
    if (!els.length) return;
    const animate = (el) => {
      const target = parseFloat(el.dataset.count);
      const decimals = el.dataset.count.includes(".") ? 1 : 0;
      const suffix = el.dataset.suffix || "";
      const dur = 1200;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = (target * eased).toFixed(decimals) + suffix;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (!("IntersectionObserver" in window)) {
      els.forEach(animate);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 }
    );
    els.forEach((el) => {
      el.setAttribute("data-count-bound", "1");
      io.observe(el);
    });
  }

  /* ---------------- toast ---------------- */
  let toastTimer;
  function showToast(title, subtitle) {
    let toast = $(".e-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "e-toast";
      toast.innerHTML =
        '<span class="e-toast__ico"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M5 13l4 4L19 7"/></svg></span>' +
        '<span><b class="e-toast__title"></b><span class="e-toast__sub"></span></span>';
      document.body.appendChild(toast);
    }
    $(".e-toast__title", toast).textContent = title;
    $(".e-toast__sub", toast).textContent = subtitle || "";
    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove("is-visible"), 3200);
  }
  window.EmberaToast = showToast;

  /* ---------------- real Shopify cart (Ajax Cart API) ---------------- */
  function updateCartBadge(count) {
    $$(".e-cart-count").forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function addToCart(id, qty) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: id, quantity: qty || 1 }] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || "Could not add this item to your cart.");
    }
    const cartRes = await fetch("/cart.js");
    const cart = await cartRes.json();
    updateCartBadge(cart.item_count);
    return cart;
  }
  window.EmberaCart = { addToCart, updateCartBadge };

  function initAddToCartForms() {
    $$("form[data-atc-form]:not([data-bound])").forEach((form) => {
      form.dataset.bound = "1";
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const btn = $("[type=submit]", form);
        const idInput = $("input[name=id]", form);
        const qtyInput = $("input[name=quantity]", form);
        const productName = form.dataset.productName || "Item";
        if (!idInput) return;
        const originalLabel = btn ? btn.innerHTML : "";
        if (btn) {
          btn.disabled = true;
          btn.innerHTML = "Adding…";
        }
        try {
          await addToCart(idInput.value, qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1);
          showToast("Added to cart", productName);
        } catch (err) {
          showToast("Couldn't add to cart", err.message);
        } finally {
          if (btn) {
            btn.disabled = false;
            btn.innerHTML = originalLabel;
          }
        }
      });
    });
  }

  /* ---------------- init ---------------- */
  document.addEventListener("DOMContentLoaded", () => {
    initHeader();
    initHeroSplit();
    initReveal();
    initTilt();
    initMagnetic();
    initParallax();
    initAccordion(document);
    initTabs(document);
    initCompare();
    initCounters();
    initAddToCartForms();
  });

  window.EmberaUI = { initAccordion, initTabs, initReveal, initTilt, initMagnetic, initCompare, initCounters, splitTitle, initAddToCartForms };
})();
